if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
          optionOpen: 'sticky-atc-option--open',
          panelAlignEnd: 'sticky-atc-option__panel--align-end',
        };

        this.submitEl.addEventListener('click', this.onClick.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      get optionSelects() {
        return (this._optionSelects = this._optionSelects || Array.from(this.querySelectorAll('.sticky-atc-option')));
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');

        this.initOptionSelects();
        this.init();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.onDocumentClick) {
          document.removeEventListener('click', this.onDocumentClick);
        }

        if (this.onDocumentKeydown) {
          document.removeEventListener('keydown', this.onDocumentKeydown);
        }
      }

      initOptionSelects() {
        if (!this.optionSelects.length) return;

        const dataEl = this.querySelector('script[type="application/json"]');
        try {
          this.variantData = dataEl ? JSON.parse(dataEl.textContent) : [];
        } catch (error) {
          this.variantData = [];
        }

        this.selectedOptions = this.optionSelects.map((optionEl) => {
          const selected =
            optionEl.querySelector('[role="option"][aria-selected="true"]') || optionEl.querySelector('[role="option"]');
          return selected ? selected.dataset.value : null;
        });

        this.optionSelects.forEach((optionEl, index) => {
          const toggle = optionEl.querySelector('.sticky-atc-option__toggle');

          toggle.addEventListener('click', () => this.togglePanel(optionEl));

          optionEl.querySelectorAll('[role="option"]').forEach((item) => {
            item.addEventListener('click', () => {
              this.closePanels();
              toggle.focus();
              this.selectOptionValue(index, item.dataset.value);
            });
          });

          optionEl.addEventListener('keydown', (event) => this.onOptionKeydown(event, optionEl, index));
        });

        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.closePanels();
        };
        this.onDocumentKeydown = (event) => {
          if (event.key === 'Escape') this.closePanels(true);
        };
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);

        this.refreshOptionSelects();
      }

      togglePanel(optionEl) {
        const panel = optionEl.querySelector('[role="listbox"]');
        if (panel.hidden) {
          this.openPanel(optionEl);
        } else {
          this.closePanels();
        }
      }

      openPanel(optionEl) {
        this.closePanels();

        const toggle = optionEl.querySelector('.sticky-atc-option__toggle');
        const panel = optionEl.querySelector('[role="listbox"]');

        optionEl.classList.add(this.classes.optionOpen);
        toggle.setAttribute('aria-expanded', 'true');
        panel.hidden = false;

        // Keep the panel inside the viewport.
        panel.classList.remove(this.classes.panelAlignEnd);
        const rect = panel.getBoundingClientRect();
        if (rect.right > window.innerWidth - 12) {
          panel.classList.add(this.classes.panelAlignEnd);
        }

        const selected =
          panel.querySelector('[role="option"][aria-selected="true"]') || panel.querySelector('[role="option"]');
        if (selected) selected.focus();
      }

      closePanels(focusToggle = false) {
        this.optionSelects.forEach((optionEl) => {
          const panel = optionEl.querySelector('[role="listbox"]');
          if (panel.hidden) return;

          panel.hidden = true;
          optionEl.classList.remove(this.classes.optionOpen);

          const toggle = optionEl.querySelector('.sticky-atc-option__toggle');
          toggle.setAttribute('aria-expanded', 'false');
          if (focusToggle) toggle.focus();
        });
      }

      onOptionKeydown(event, optionEl, index) {
        const panel = optionEl.querySelector('[role="listbox"]');
        const items = Array.from(panel.querySelectorAll('[role="option"]'));

        if (panel.hidden) {
          if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            this.openPanel(optionEl);
          }
          return;
        }

        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowUp': {
            event.preventDefault();
            const current = items.indexOf(document.activeElement);
            const direction = event.key === 'ArrowDown' ? 1 : -1;
            const next =
              current === -1 ? (direction === 1 ? 0 : items.length - 1) : (current + direction + items.length) % items.length;
            items[next].focus();
            break;
          }
          case 'Home':
            event.preventDefault();
            items[0].focus();
            break;
          case 'End':
            event.preventDefault();
            items[items.length - 1].focus();
            break;
          case 'Enter':
          case ' ': {
            if (items.includes(document.activeElement)) {
              event.preventDefault();
              const value = document.activeElement.dataset.value;
              this.closePanels(true);
              this.selectOptionValue(index, value);
            }
            break;
          }
          case 'Tab':
            this.closePanels();
            break;
        }
      }

      findVariant(options) {
        return this.variantData.find((item) => item.options.every((option, index) => option === options[index]));
      }

      selectOptionValue(index, value) {
        if (!value || this.selectedOptions[index] === value) return;

        const desired = [...this.selectedOptions];
        desired[index] = value;

        let variant = this.findVariant(desired);
        if (!variant) {
          // The exact combination doesn't exist - jump to the closest variant carrying this value.
          const candidates = this.variantData.filter((item) => item.options[index] === value);
          if (!candidates.length) return;

          const score = (item) =>
            item.options.reduce(
              (acc, option, optionIndex) => acc + (optionIndex !== index && option === desired[optionIndex] ? 2 : 0),
              0
            ) + (item.available ? 1 : 0);
          variant = candidates.sort((a, b) => score(b) - score(a))[0];
        }

        this.selectedOptions = [...variant.options];
        this.refreshOptionSelects();
        this.applyVariant(variant);
      }

      refreshOptionSelects() {
        if (!this.optionSelects.length) return;

        this.optionSelects.forEach((optionEl, index) => {
          const valueEl = optionEl.querySelector('[data-option-value]');
          if (valueEl) valueEl.textContent = this.selectedOptions[index];

          optionEl.querySelectorAll('[role="option"]').forEach((item) => {
            const value = item.dataset.value;
            item.setAttribute('aria-selected', value === this.selectedOptions[index] ? 'true' : 'false');

            const optionsToCheck = [...this.selectedOptions];
            optionsToCheck[index] = value;
            const match = this.findVariant(optionsToCheck);

            item.classList.toggle('is-unavailable', !match);
            item.classList.toggle('is-soldout', Boolean(match) && !match.available);

            const statusEl = item.querySelector('.sticky-atc-option__item-status');
            if (statusEl) statusEl.textContent = match && !match.available ? FoxTheme.variantStrings.soldOut : '';
          });
        });
      }

      applyVariant(variant) {
        const mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
        if (mainVariantSelects) {
          Array.from(mainVariantSelects.querySelectorAll('select, fieldset')).forEach((element, index) => {
            const optionValue = variant.options[index];
            if (element.tagName === 'SELECT') {
              element.value = optionValue;
              element.querySelectorAll('option').forEach((option) => {
                option.toggleAttribute('selected', option.value === optionValue);
              });
            } else {
              element.querySelectorAll('input').forEach((input) => {
                input.checked = input.value === optionValue;
              });
            }
          });
          mainVariantSelects.dispatchEvent(new Event('change'));
        } else {
          const mainForm = document.getElementById(`product-form-${this.dataset.section}`);
          const idInput = mainForm && mainForm.querySelector('input[name="id"]');
          if (idInput) {
            idInput.value = variant.id;
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.updatePrice(variant);
          this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
        }
      }

      init() {
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle(this.classes.isOpen, isAboveViewport);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      onClick() {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('.btn__text');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCartShort;
        }
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          if (!variant) {
            this.updateButton(true, '', true);
            return;
          }

          if (this.optionSelects.length && variant.options) {
            this.selectedOptions = [...variant.options];
            this.refreshOptionSelects();
          }

          this.updatePrice(variant);
          this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
        });
      }
    }
  );
}
